import type { Breadcrumb, BrowserOptions, ErrorEvent, replayIntegration } from '@sentry/angular';

/** The event `beforeSendTransaction` receives; the SDK does not export this type by name. */
export type TransactionEvent = Parameters<NonNullable<BrowserOptions['beforeSendTransaction']>>[0];

type ReplayOptions = NonNullable<Parameters<typeof replayIntegration>[0]>;

/** A replay recording frame as `beforeAddRecordingEvent` receives it. */
export type RecordingEvent = Parameters<NonNullable<ReplayOptions['beforeAddRecordingEvent']>>[0];

export const SENTRY_REDACTED = '[redacted]';

export const SENTRY_TRUNCATED = '[truncated]';

/** Routes whose next path segment is a bearer credential (public share links, password reset, contact cabinet api). */
export const SENTRY_TOKEN_ROUTES: readonly string[] = ['share', 'reset-password', 'cabinet'];

/** SPA routes that carry their credential in the query string (`/contact-cabinet?token=`, invitations). */
export const SENTRY_TOKEN_QUERY_ROUTES: readonly string[] = ['contact-cabinet', 'accept-invitation'];

const MAX_DATA_DEPTH = 5;
/** rrweb's custom-event type: Sentry's breadcrumb and performance-span frames inside a replay. */
const RRWEB_CUSTOM_EVENT = 5;
/** Same key list as the backend's SecretRedactor, so both sides agree on what a secret field is. */
const SENSITIVE_KEY =
    /pass(word)?|secret|token|api[_-]?key|authorization|cookie|credential|private[_-]?key|dsn|signature/i;
const TOKEN_PATH = new RegExp(`/(${SENTRY_TOKEN_ROUTES.join('|')})/[^/?#\\s]+`, 'g');
const TOKEN_ROUTE = new RegExp(`(^|/)(${SENTRY_TOKEN_ROUTES.join('|')})/[^/?#]+`);
const SECRET_PARAM = /\b([\w-]*(?:token|api[_-]?key|signature|secret|password))=[^&#\s]+/gi;
const QUERY_CODE = /([?&]code)=[^&#\s]+/gi;
const VALUE_PATTERNS: ReadonlyArray<[RegExp, string]> = [
    [/\bsk-[A-Za-z0-9_-]+/g, SENTRY_REDACTED],
    [/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, `Bearer ${SENTRY_REDACTED}`],
    [/\beyJ[\w-]+\.[\w-]+\.[\w-]+/g, SENTRY_REDACTED]
];

export function scrubUrl(url: string): string {
    return url
        .replace(TOKEN_PATH, `/$1/${SENTRY_REDACTED}`)
        .replace(SECRET_PARAM, `$1=${SENTRY_REDACTED}`)
        .replace(QUERY_CODE, `$1=${SENTRY_REDACTED}`);
}

export function scrubText(text: string): string {
    return VALUE_PATTERNS.reduce(
        (result, [pattern, replacement]) => result.replace(pattern, replacement),
        scrubUrl(text)
    );
}

export function isTokenRoute(url: string): boolean {
    const segments = url.split(/[?#]/)[0].split('/');

    return TOKEN_ROUTE.test(url) || segments.some(segment => SENTRY_TOKEN_QUERY_ROUTES.includes(segment));
}

export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
    return {
        ...breadcrumb,
        message: breadcrumb.message ? scrubText(breadcrumb.message) : breadcrumb.message,
        data: breadcrumb.data ? scrubData(breadcrumb.data) : breadcrumb.data
    };
}

export function scrubEvent(event: ErrorEvent): ErrorEvent {
    return {
        ...event,
        message: event.message ? scrubText(event.message) : event.message,
        transaction: event.transaction ? scrubUrl(event.transaction) : event.transaction,
        request: scrubRequest(event.request),
        extra: event.extra ? scrubData(event.extra) : event.extra,
        contexts: event.contexts ? (scrubData(event.contexts) as typeof event.contexts) : event.contexts,
        tags: event.tags ? (scrubData(event.tags) as typeof event.tags) : event.tags,
        exception: event.exception?.values
            ? {
                  ...event.exception,
                  values: event.exception.values.map(value =>
                      value.value ? { ...value, value: scrubText(value.value) } : value
                  )
              }
            : event.exception,
        breadcrumbs: event.breadcrumbs?.map(scrubBreadcrumb)
    };
}

/** Transactions carry the page URL and every request URL in their spans, so they are scrubbed too. */
export function scrubTransaction(event: TransactionEvent): TransactionEvent {
    return {
        ...event,
        transaction: event.transaction ? scrubUrl(event.transaction) : event.transaction,
        request: scrubRequest(event.request),
        // the pageload root span keeps the full page url in contexts.trace.data (url.full, url.path, lcp.url)
        contexts: event.contexts ? (scrubData(event.contexts) as typeof event.contexts) : event.contexts,
        extra: event.extra ? scrubData(event.extra) : event.extra,
        tags: event.tags ? (scrubData(event.tags) as typeof event.tags) : event.tags,
        spans: event.spans?.map(span => ({
            ...span,
            description: span.description ? scrubText(span.description) : span.description,
            data: span.data ? (scrubData(span.data) as typeof span.data) : span.data
        })),
        breadcrumbs: event.breadcrumbs?.map(scrubBreadcrumb)
    };
}

/**
 * Replay frames bypass beforeSend. Only Sentry's custom frames (console/xhr breadcrumbs, network
 * spans) are scrubbed: DOM snapshot frames are already masked and must keep their full depth.
 */
export function scrubRecordingEvent(event: RecordingEvent): RecordingEvent {
    const frame = event as unknown as { type: number; data?: { payload?: unknown } };

    if (frame.type !== RRWEB_CUSTOM_EVENT || frame.data?.payload === undefined) {
        return event;
    }

    return {
        ...frame,
        data: { ...frame.data, payload: scrubValue(frame.data.payload, 0) }
    } as unknown as RecordingEvent;
}

function scrubRequest<T extends { url?: string; headers?: Record<string, string> } | undefined>(request: T): T {
    if (!request) {
        return request;
    }

    return {
        ...request,
        url: request.url ? scrubUrl(request.url) : request.url,
        headers: request.headers ? scrubData(request.headers) : request.headers
    } as T;
}

function scrubData(data: Record<string, unknown>): Record<string, unknown> {
    return scrubValue(data, 0) as Record<string, unknown>;
}

function scrubValue(value: unknown, depth: number): unknown {
    if (typeof value === 'string') {
        return scrubText(value);
    }

    if (!Array.isArray(value) && !isPlainObject(value)) {
        return value;
    }

    if (depth >= MAX_DATA_DEPTH) {
        return SENTRY_TRUNCATED;
    }

    return Array.isArray(value)
        ? value.map(item => scrubValue(item, depth + 1))
        : Object.fromEntries(
              Object.entries(value).map(([key, item]) => [
                  key,
                  SENSITIVE_KEY.test(key) ? SENTRY_REDACTED : scrubValue(item, depth + 1)
              ])
          );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const prototype = Object.getPrototypeOf(value);

    return prototype === Object.prototype || prototype === null;
}
